/* @format */

import { RegisteredDealService } from '@/scripts/services/entities/registered-deal-service';
import { SharedAssetService } from '@/scripts/services/entities/shared-asset-service';
import { ClientManagementService } from '@/scripts/services/mdf/client/client-management-service';

export const RegisteredDealWidgetService = {
    /**
     * Get the registered deal by ID.
     *
     * @param {number} registeredDealId - The ID of the registered deal.
     */
    async getRegisteredDealById(registeredDealId) {
        return RegisteredDealService.getRegisteredDealById({
            id: registeredDealId,
            query: {
                select: [
                    'Id',
                    'Name',
                    'RegistrationNumber',
                    'FundRequestId',
                    'FundRequestName',
                    'IsApproved',
                    'Description',
                    'Value',
                    'Agency',
                    'ProjectedClosingDate',
                ],
                expand: [
                    {
                        Deal: {
                            select: [
                                'Id',
                                'Name',
                                'CreatedByUser',
                                'Company',
                                'Contact',
                                'DealStatus',
                                'Account',
                                'LineItems',
                            ],
                            expand: [
                                {
                                    DealStatus: {
                                        select: ['Name', 'Color'],
                                    },
                                    CreatedByUser: {
                                        select: ['FirstName', 'LastName', 'ProfileImageUrl'],
                                    },
                                    Contact: {
                                        select: ['FirstName', 'LastName', 'ProfileImageUrl'],
                                        expand: {
                                            Company: {
                                                select: ['Name', 'LogoUrl'],
                                            },
                                        },
                                    },
                                    Account: {
                                        select: ['Client'],
                                        expand: [
                                            {
                                                Client: {
                                                    select: ['Id', 'Name', 'LogoImageUrl'],
                                                },
                                            },
                                        ],
                                    },
                                },
                            ],
                        },
                        Agency: {
                            select: ['Id', 'Name', 'LogoImageUrl'],
                        },
                        RegisteredDealAgencySolutions: {
                            select: ['Id', 'UnitCount', 'AgencySolution'],
                            expand: {
                                AgencySolution: {
                                    select: ['Id', 'Name'],
                                },
                            },
                        },
                    },
                ],
            },
        }).then((res) => res.data.value[0]);
    },

    /**
     * Create a new registered deal.
     *
     * @param {Object} registeredDeal
     */
    async postRegisteredDeal(registeredDeal) {
        const registeredDealDto = this.mapToPostRegisteredDealDto(registeredDeal);
        return RegisteredDealService.postRegisteredDeal(registeredDealDto);
    },

    /**
     * Update a registered deal.
     *
     * @param {number} id - The ID of the registered deal.
     * @param {Object} originalRegisteredDeal - The original registered deal object.
     * @param {Object} updatedRegisteredDeal - The updated registered deal object.
     */
    async patchRegisteredDeal(id, updatedRegisteredDeal) {
        const registeredDealChanges = this.mapToPatchRegisteredDealChanges(updatedRegisteredDeal);
        return RegisteredDealService.patchRegisteredDeal(id, registeredDealChanges);
    },

    /**
     * Get the available agency solutions by Agency ID.
     *
     * @param {number} agencyId - The ID of the agency.
     * @param {number} clientId - The ID of the client.
     */
    async getAgencySolutionsByAgencyId(agencyId, clientId) {
        return SharedAssetService.getSharedAssets({
            query: {
                select: ['Solution'],
                filter: {
                    SolutionId: {
                        ne: null,
                    },
                    AgencyClient: {
                        AgencyId: agencyId,
                        ClientId: clientId,
                    },
                },
                expand: {
                    Solution: {
                        select: ['Id', 'Name'],
                    },
                },
            },
        }).then((res) => res?.data?.value ?? []);
    },

    /**
     * Get fund requests by client account ID and agency ID.
     *
     * @param {number} clientAccountId
     * @param {number} agencyId
     */
    async getMdfRequests(clientAccountId, agencyId) {
        const config = {
            filter: JSON.stringify([
                {
                    AgencyId: agencyId,
                },
            ]),
        };

        return ClientManagementService.getAll({ accountId: clientAccountId, config }).then(
            (res) => res?.data?.data ?? [],
        );
    },

    /**
     * Map a registered deal to a registered deal DTO for the POST endpoint.
     *
     * @param {Object} registeredDeal
     */
    mapToPostRegisteredDealDto(registeredDeal) {
        return {
            Name: registeredDeal.name,
            IsApproved: registeredDeal.isApproved,
            Value: registeredDeal.amount,
            Description: registeredDeal.description,
            RegistrationNumber: registeredDeal.registrationNumber,
            Reason: registeredDeal.reason ?? null,
            DealId: registeredDeal.opportunity.id,
            AgencyId: registeredDeal.agency.id,
            FundRequestId: registeredDeal.fundRequestId,
            FundRequestName: registeredDeal.fundRequestName,
            ProjectedClosingDate: registeredDeal.projectedCloseDate,
            RegisteredDealAgencySolutions: registeredDeal.agencySolutions.map((agencySolution) => ({
                UnitCount: agencySolution.unitCount,
                AgencySolutionId: agencySolution.solutionId,
            })),
        };
    },

    mapToPatchRegisteredDealChanges(registeredDeal) {
        const updatedDto = this.mapToPatchRegisteredDealDto(registeredDeal);

        const changes = Object.entries(updatedDto).map(([key, val]) => ({
            op: 'replace',
            name: key,
            value: val,
        }));

        return changes;
    },

    /**
     * Map a registered deal to a registered deal DTO for the PATCH endpoint.
     
     * @param {Object} registeredDeal
     */
    mapToPatchRegisteredDealDto(registeredDeal) {
        return {
            Id: registeredDeal.id,
            Name: registeredDeal.name,
            IsApproved: registeredDeal.isApproved,
            Value: registeredDeal.amount,
            Description: registeredDeal.description,
            RegistrationNumber: registeredDeal.registrationNumber,
            Reason: registeredDeal.reason ?? null,
            DealId: registeredDeal.opportunity.id,
            AgencyId: registeredDeal.agency.id,
            FundRequestId: registeredDeal.fundRequestId,
            FundRequestName: registeredDeal.fundRequestName,
            ProjectedClosingDate: registeredDeal.projectedCloseDate,
            RegisteredDealAgencySolutions: registeredDeal.agencySolutions.map((agencySolution) => ({
                RegisteredDealId: registeredDeal.id,
                UnitCount: agencySolution.unitCount,
                AgencySolutionId: agencySolution.solutionId,
            })),
        };
    },
};
